import { handleActions } from 'redux-actions';
import update from 'react-addons-update';
import * as actions from './actions';

const initialState = {
  activeStep: null,
  error: {},
  isLoading: false,
  isUpdating: false,
  options: [],
  order: {},
  nextPage: null,
  list: [],
  paymentMethod: undefined,
  success: {},
};

const setFetchPaymentProductsRequest = (state) =>
  update(state, {
    error: { $set: {} },
    isLoading: { $set: true },
    success: { $set: {} },
  });

const setFetchPaymentProductsSuccess = (state, action) => {
  const initStep = typeof state.activeStep === 'number' ? state.activeStep : 0;
  return update(state, {
    isLoading: { $set: false },
    options: { $set: action.payload.options },
    activeStep: { $set: initStep },
  });
};

const setFetchPaymentProductsFailure = (state, action) =>
  update(state, {
    error: { $set: action.payload.error },
    isLoading: { $set: false },
  });

const setPostPaymentOrderRequest = (state) =>
  update(state, {
    isUpdating: { $set: true },
  });

const setPostPaymentOrderSuccess = (state, action) => {
  const current = typeof state.activeStep === 'number' ? state.activeStep : -Infinity;
  const nextStep = Math.max(current, 1);
  return update(state, {
    activeStep: { $set: nextStep },
    isUpdating: { $set: false },
    order: { $set: action.payload.order },
  });
};

const setPostPaymentOrderFailure = (state, action) => {
  // If we somehow already reached Done, don't regress on a late failure
  const nextStep = state.activeStep === 2 ? 2 : 0;
  return update(state, {
    error: { $set: action.payload.error },
    isUpdating: { $set: false },
    activeStep: { $set: nextStep },
  });
};

const setFetchPaymentOrderRequest = (state) =>
  update(state, {
    isLoading: { $set: true },
  });

const setFetchPaymentOrderSuccess = (state, action) => {
  const { order } = action.payload;
  const isPaid = !!order?.paid_at || String(order?.payment_status || '').toLowerCase() === 'paid';
  const suggested = isPaid ? 2 : 1;
  const current = typeof state.activeStep === 'number' ? state.activeStep : null;
  const nextStep = current === null ? suggested : Math.max(current, suggested);
  return update(state, {
    isLoading: { $set: false },
    order: { $set: order },
    activeStep: { $set: nextStep },
  });
};

const setFetchPaymentOrderFailure = (state, action) =>
  update(state, {
    error: { $set: action.payload.error },
    isLoading: { $set: false },
  });

const setPostPaymentPayRequest = (state) =>
  update(state, {
    isUpdating: { $set: true },
  });

const setPostPaymentPaySuccess = (state, action) =>
  update(state, {
    activeStep: { $set: 2 },
    isUpdating: { $set: false },
    order: { $set: action.payload.order },
  });

const setPostPaymentPayFailure = (state, action) =>
  update(state, {
    activeStep: { $set: 0 },
    error: { $set: action.payload.error },
    isUpdating: { $set: false },
  });

const setPostPaymentInvoiceRequest = (state) =>
  update(state, {
    isUpdating: { $set: true },
  });

const setPostPaymentInvoiceSuccess = (state) =>
  update(state, {
    activeStep: { $set: 2 },
    isUpdating: { $set: false },
    success: { $set: { code: 'informationSaved' } },
  });

const setPostPaymentInvoiceFailure = (state, action) =>
  update(state, {
    error: { $set: action.payload.error },
    isUpdating: { $set: false },
  });

const setActiveStep = (state, action) => {
  const next = Number(action.payload.activeStep);
  const force = Boolean(action.payload.force);
  const current = typeof state.activeStep === 'number' ? state.activeStep : -Infinity;

  const value = force ? next : Math.max(current, next);

  return update(state, {
    activeStep: { $set: value },
  });
};

const setPaymentMethod = (state, action) =>
  update(state, {
    paymentMethod: { $set: action.payload.paymentMethod },
  });

const setIsUpdating = (state, action) =>
  update(state, {
    isUpdating: { $set: action.payload.isUpdating },
  });

const resetFlow = (state) =>
  update(state, {
    activeStep: { $set: 0 },
    error: { $set: {} },
    success: { $set: {} },
    order: { $set: {} },
    paymentMethod: { $set: undefined },
  });

const cancelCheckout = (state) =>
  update(state, {
    activeStep: { $set: 1 }, // back to payment step (form)
    isUpdating: { $set: false }, // ensure buttons re-enabled
    order: { $set: {} }, // drop transient order
    paymentMethod: { $set: undefined },
    error: { $set: {} },
    success: { $set: {} },
  });

const setFetchOrdersRequest = (state) =>
  update(state, {
    error: { $set: {} },
    isLoading: { $set: true },
    nextPage: { $set: null },
    success: { $set: {} },
  });

const setFetchOrdersSuccess = (state, action) =>
  update(state, {
    isLoading: { $set: false },
    list: { $set: action.payload.reset ? [] : state.list.concat(action.payload.list) },
    nextPage: { $set: action.payload.nextPage },
  });

const setFetchOrdersFailure = (state, action) =>
  update(state, {
    error: { $set: action.payload.error },
    isLoading: { $set: false },
    nextPage: { $set: null },
  });

export default handleActions(
  {
    [actions.FETCH_PAYMENT_PRODUCTS_REQUEST]: setFetchPaymentProductsRequest,
    [actions.FETCH_PAYMENT_PRODUCTS_SUCCESS]: setFetchPaymentProductsSuccess,
    [actions.FETCH_PAYMENT_PRODUCTS_FAILURE]: setFetchPaymentProductsFailure,

    [actions.POST_PAYMENT_ORDER_REQUEST]: setPostPaymentOrderRequest,
    [actions.POST_PAYMENT_ORDER_SUCCESS]: setPostPaymentOrderSuccess,
    [actions.POST_PAYMENT_ORDER_FAILURE]: setPostPaymentOrderFailure,

    [actions.FETCH_PAYMENT_ORDER_REQUEST]: setFetchPaymentOrderRequest,
    [actions.FETCH_PAYMENT_ORDER_SUCCESS]: setFetchPaymentOrderSuccess,
    [actions.FETCH_PAYMENT_ORDER_FAILURE]: setFetchPaymentOrderFailure,

    [actions.POST_PAYMENT_PAY_REQUEST]: setPostPaymentPayRequest,
    [actions.POST_PAYMENT_PAY_SUCCESS]: setPostPaymentPaySuccess,
    [actions.POST_PAYMENT_PAY_FAILURE]: setPostPaymentPayFailure,

    [actions.POST_PAYMENT_INVOICE_REQUEST]: setPostPaymentInvoiceRequest,
    [actions.POST_PAYMENT_INVOICE_SUCCESS]: setPostPaymentInvoiceSuccess,
    [actions.POST_PAYMENT_INVOICE_FAILURE]: setPostPaymentInvoiceFailure,

    [actions.PAYMENT_SET_ACTIVE_STEP]: setActiveStep,
    [actions.PAYMENT_SET_METHOD]: setPaymentMethod,
    [actions.PAYMENT_SET_IS_UPDATING]: setIsUpdating,
    [actions.PAYMENT_RESET_FLOW]: resetFlow,
    [actions.PAYMENT_CANCEL_CHECKOUT]: cancelCheckout,

    [actions.FETCH_ORDERS_REQUEST]: setFetchOrdersRequest,
    [actions.FETCH_ORDERS_SUCCESS]: setFetchOrdersSuccess,
    [actions.FETCH_ORDERS_FAILURE]: setFetchOrdersFailure,
  },
  initialState,
);
